import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Settings, Mail, CheckCircle, AlertCircle } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';

const SendmireinSettings = () => {
  const { t } = useTranslation();
  const { token } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  
  const [formData, setFormData] = useState({
    host: '',
    port: 587,
    secure: false,
    user: '',
    password: '',
    fromEmail: '',
    fromName: '',
    enabled: true,
  });

  useEffect(() => {
    if (token) loadSettings();
  }, [token]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await api.newslettersSmtpGet(token);;
      
      if (response) {
        setFormData({
          host: response.host || '',
          port: response.port || 587,
          secure: response.secure || false,
          user: response.user || '',
          password: '', // Never send password back from server
          fromEmail: response.fromEmail || '',
          fromName: response.fromName || '',
          enabled: response.enabled !== false,
        });
      }
    } catch (error) {
      console.error('Error loading SMTP settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setTestResult(null); // Reset test result when changing settings
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Validate
      if (!formData.host || !formData.user || !formData.fromEmail) {
        toast({
          title: t('sendmirein.common.error'),
          description: t('sendmirein.settings.fillRequired'),
          variant: 'destructive',
        });
        return;
      }

      await api.newslettersSmtpSave(token, formData);
      
      toast({
        title: t('sendmirein.common.success'),
        description: t('sendmirein.settings.saved'),
      });
      
      loadSettings(); // Reload to get updated data
    } catch (error) {
      toast({
        title: t('sendmirein.common.error'),
        description: error.response?.data?.error || t('sendmirein.settings.saveFailed'),
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    try {
      setTesting(true);
      setTestResult(null);
      
      const response = await api.newslettersSmtpTest(token);
      
      setTestResult({
        success: response.success,
        message: response.message,
      });
      
      toast({
        title: t('sendmirein.common.success'),
        description: t('sendmirein.settings.testSuccessToast'),
      });
    } catch (error) {
      setTestResult({
        success: false,
        message: error.message || 'Verbindung fehlgeschlagen',
      });
      
      toast({
        title: t('sendmirein.common.error'),
        description: t('sendmirein.settings.testFailedToast'),
        variant: 'destructive',
      });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-muted p-6">
        <div className="max-w-4xl mx-auto">
          <p className="text-center text-muted-foreground">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted">
      <div className="max-w-4xl mx-auto p-6">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
            <Settings className="w-8 h-8" />
            {t('sendmirein.settings.title')}
          </h1>
          <p className="text-muted-foreground mt-1">{t('sendmirein.settings.subtitle')}</p>
        </div>
        <Card className="p-6">
          <div className="space-y-6">
            <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <Mail className="w-5 h-5 text-blue-600 mt-0.5" />
              <div className="text-sm text-blue-900">
                <p className="font-medium mb-1">{t('sendmirein.settings.ownSmtpTitle')}</p>
                <p className="text-blue-700">{t('sendmirein.settings.ownSmtpDesc')}</p>
              </div>
            </div>

            {/* SMTP Host */}
            <div>
              <Label htmlFor="host">{t('sendmirein.settings.fields.host')}</Label>
              <Input
                id="host"
                type="text"
                placeholder={t('sendmirein.settings.placeholders.host')}
                value={formData.host}
                onChange={(e) => handleChange('host', e.target.value)}
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                {t('sendmirein.settings.help.hostExamples')}
              </p>
            </div>

            {/* Port & Secure */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="port">{t('sendmirein.settings.fields.port')}</Label>
                <Input
                  id="port"
                  type="number"
                  value={formData.port}
                  onChange={(e) => handleChange('port', parseInt(e.target.value))}
                  className="mt-1"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {t('sendmirein.settings.help.port')}
                </p>
              </div>
              
              <div>
                <Label htmlFor="secure">{t('sendmirein.settings.fields.secure')}</Label>
                <select
                  id="secure"
                  value={formData.secure ? 'true' : 'false'}
                  onChange={(e) => handleChange('secure', e.target.value === 'true')}
                  className="mt-1 w-full px-3 py-2 border border-border rounded-md"
                >
                  <option value="false">{t('sendmirein.settings.secure.tls')}</option>
                  <option value="true">{t('sendmirein.settings.secure.ssl')}</option>
                </select>
              </div>
            </div>

            {/* Username */}
            <div>
              <Label htmlFor="user">{t('sendmirein.settings.fields.user')}</Label>
              <Input
                id="user"
                type="text"
                placeholder={t('sendmirein.settings.placeholders.user')}
                value={formData.user}
                onChange={(e) => handleChange('user', e.target.value)}
                className="mt-1"
              />
            </div>

            {/* Password */}
            <div>
              <Label htmlFor="password">{t('sendmirein.settings.fields.password')}</Label>
              <Input
                id="password"
                type="password"
                placeholder={formData.password ? '••••••••' : t('sendmirein.settings.placeholders.password')}
                value={formData.password}
                onChange={(e) => handleChange('password', e.target.value)}
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                {t('sendmirein.settings.help.passwordLeaveEmpty')}
              </p>
            </div>

            {/* From Email */}
            <div>
              <Label htmlFor="fromEmail">{t('sendmirein.settings.fields.fromEmail')}</Label>
              <Input
                id="fromEmail"
                type="email"
                placeholder={t('sendmirein.settings.placeholders.fromEmail')}
                value={formData.fromEmail}
                onChange={(e) => handleChange('fromEmail', e.target.value)}
                className="mt-1"
              />
            </div>

            {/* From Name */}
            <div>
              <Label htmlFor="fromName">{t('sendmirein.settings.fields.fromName')}</Label>
              <Input
                id="fromName"
                type="text"
                placeholder={t('sendmirein.settings.placeholders.fromName')}
                value={formData.fromName}
                onChange={(e) => handleChange('fromName', e.target.value)}
                className="mt-1"
              />
            </div>

            {/* Test Result */}
            {testResult && (
              <div className={`p-4 rounded-lg border ${
                testResult.success 
                  ? 'bg-green-50 border-green-200' 
                  : 'bg-red-50 border-red-200'
              }`}>
                <div className="flex items-start gap-3">
                  {testResult.success ? (
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
                  )}
                  <div>
                    <p className={`font-medium ${
                      testResult.success ? 'text-green-900' : 'text-red-900'
                    }`}>
                      {testResult.success ? t('sendmirein.settings.testSuccessTitle') : t('sendmirein.settings.testFailedTitle')}
                    </p>
                    <p className={`text-sm mt-1 ${
                      testResult.success ? 'text-green-700' : 'text-red-700'
                    }`}>
                      {testResult.message}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center justify-between pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => navigate('/sendmirein')}
              >
                {t('sendmirein.common.back')}
              </Button>
              
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={handleTest}
                  disabled={testing || !formData.host || !formData.user}
                >
                  {testing ? t('sendmirein.settings.testing') : t('sendmirein.settings.testConnection')}
                </Button>
                
                <Button
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? t('sendmirein.common.saving') : t('sendmirein.common.save')}
                </Button>
              </div>
            </div>
          </div>
        </Card>

        {/* Help Card */}
        <Card className="p-6 mt-6">
          <h3 className="font-semibold mb-3">{t('sendmirein.settings.help.title')}</h3>
          <div className="space-y-3 text-sm">
            <div>
              <p className="font-medium">Gmail</p>
              <p className="text-muted-foreground">Host: smtp.gmail.com | Port: 587 (TLS)</p>
              <p className="text-xs text-muted-foreground mt-1">
                Hinweis: App-Passwort erforderlich (Google-Konto → Sicherheit → App-Passwörter)
              </p>
            </div>
            <div>
              <p className="font-medium">Microsoft 365 / Outlook</p>
              <p className="text-muted-foreground">Host: smtp.office365.com | Port: 587 (TLS)</p>
            </div>
            <div>
              <p className="font-medium">Eigener Server / Hosting</p>
              <p className="text-muted-foreground">Kontaktieren Sie Ihren Hosting-Anbieter für die SMTP-Zugangsdaten</p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default SendmireinSettings;
